import React, { useMemo, useState } from 'react';
import compact from 'lodash/compact';
import xor from 'lodash/xor';
import {
  ActionLayer,
  Divider,
  FormDropdownOption,
} from '@sparknz/spark-pattern-library';
import { trackCustomLink, trackDigitalEvent } from '@sparknz/tracking';

import { DeviceGalleryQuery$data } from '__generated__/DeviceGalleryQuery.graphql';
import { useQuery } from 'shared/hooks';
import { convertToArray } from 'shared/utils';
import {
  CardsContainer,
  FiltersBottomSection,
  FiltersTopSection,
  MoreFilters,
  SectionContainer,
} from './components';
import { FilterIds } from './typings';
import {
  applyFilters,
  getAvailableBrandOptions,
  getAvailableColorOptions,
  getAvailableCompatibleSimOptions,
  getAvailableNetworkCapabilityOptions,
  getAvailablePriceOptions,
  removeUnavailableFilterOptions,
} from './utils/applyFilters.utils';
import { trackApplyFilterEvent } from './utils/trackApplyFilterEvent.utils';

type DeviceGalleryData = DeviceGalleryQuery$data['deviceGallery'];

type DeviceGalleryInternalProps = {
  products: DeviceGalleryData['products'];
  filters: DeviceGalleryData['filters'];
  subcategories: DeviceGalleryData['subcategories'];
  selectedSubcategoryId: string;
  onSelectSubcategoryId: (subcategoryId: string) => void;
};

export default function DeviceGallery({
  products,
  filters,
  subcategories,
  selectedSubcategoryId,
  onSelectSubcategoryId,
}: DeviceGalleryInternalProps) {
  const [showAllFilters, setShowAllFilters] = useState(false);
  const { replaceQuery, query } = useQuery();
  const selectedBrandValues = convertToArray(query.brand);
  const selectedColorValues = convertToArray(query.primaryColor);
  const selectedNetworkCapabilityValues = convertToArray(
    query.networkCapability,
  );
  const selectedCompatibleSimValues = convertToArray(query.compatibleSim);
  const selectedPriceValue = query.price;

  const filteredProducts = useMemo(() => {
    const result = applyFilters({
      products,
      query,
    });

    return result;
  }, [products, query]);

  const brandOptions = getAvailableBrandOptions(filters, products, query);
  const colorOptions = getAvailableColorOptions(filters, products, query);
  const networkCapabilityOptions = getAvailableNetworkCapabilityOptions(
    filters,
    products,
    query,
  );
  const compatibleSimOptions = getAvailableCompatibleSimOptions(
    filters,
    products,
    query,
  );
  const priceOptions = getAvailablePriceOptions(products, query);

  const selectedBrands = brandOptions?.filter((option) => {
    return selectedBrandValues?.includes(option.value);
  });
  const selectedColors = colorOptions?.filter((option) =>
    selectedColorValues?.includes(option.value),
  );

  const selectedNetworkCapabilityOptions = networkCapabilityOptions?.filter(
    (option) => selectedNetworkCapabilityValues?.includes(option.value),
  );
  const selectedCompatibleSimOptions = compatibleSimOptions?.filter((option) =>
    selectedCompatibleSimValues?.includes(option.value),
  );

  const handleFilterChange = (
    options: FormDropdownOption<string>[],
    filterName: string,
  ) => {
    const optionValues = options.map((option) => option.value);
    const newFilterOptions = {
      ...query,
      [filterName]: optionValues,
    };
    const sanitisedFilterQuery = removeUnavailableFilterOptions(
      filters,
      products,
      newFilterOptions,
    );
    const clickedLabel = options.find(
      (option) =>
        option.value ===
        xor(compact([query[filterName]].flat()), optionValues)[0],
    )?.label;

    if (clickedLabel) trackCustomLink(clickedLabel);

    replaceQuery(sanitisedFilterQuery);
  };

  const handleShowAll = () => {
    trackCustomLink('Show all filters');

    trackDigitalEvent({
      event: 'actionLayer.view',
      w3cddl: {
        event: [
          {
            eventInfo: {
              eventName: 'Shop Mobile Phones Gallery Filters',
              eventAction: 'actionLayer.view',
            },
          },
        ],
      },
    });

    setShowAllFilters(true);
  };

  const handleCloseAllFilters = () => {
    trackCustomLink('Close Filters');

    const selectedPrice = priceOptions?.find((option) => {
      return selectedPriceValue === option.toString();
    });
    trackApplyFilterEvent({
      numFilters: Object.keys(query).length,
      selectedBrandValues,
      selectedColorValues,
      selectedPrice,
      selectedSubcategoryId,
    });
    setShowAllFilters(false);
  };

  return (
    <>
      <SectionContainer>
        <ActionLayer
          isOpen={showAllFilters}
          title="Filters"
          onClose={handleCloseAllFilters}
          status="default"
        >
          <MoreFilters
            brandOptions={brandOptions}
            colorOptions={colorOptions}
            priceOptions={priceOptions}
            networkCapabilityOptions={networkCapabilityOptions}
            compatibleSimOptions={compatibleSimOptions}
            selectedNetworkCapabilityOptions={selectedNetworkCapabilityOptions}
            selectedCompatibleSimOptions={selectedCompatibleSimOptions}
            selectedBrandOptions={selectedBrands}
            selectedColorOptions={selectedColors}
            onCloseAllFilters={handleCloseAllFilters}
            filterResultCount={filteredProducts?.length || 0}
            filters={filters}
            products={products}
          />
        </ActionLayer>
        <FiltersTopSection
          brandOptions={brandOptions}
          colorOptions={colorOptions}
          selectedBrandOptions={selectedBrands}
          selectedColorOptions={selectedColors}
          onBrandChange={(options) =>
            handleFilterChange(options, FilterIds.Brand)
          }
          onColorChange={(options) =>
            handleFilterChange(options, FilterIds.PrimaryColor)
          }
          onShowAll={handleShowAll}
          subcategories={subcategories}
          onSelectSubcategoryId={onSelectSubcategoryId}
          selectedSubcategoryId={selectedSubcategoryId}
        />
      </SectionContainer>
      <Divider />
      <SectionContainer>
        <FiltersBottomSection filters={filters} products={products} />
        <CardsContainer
          filteredProducts={filteredProducts}
          selectedSubcategoryId={selectedSubcategoryId}
        />
      </SectionContainer>
    </>
  );
}
